/* eslint-disable */
import React, { ReactNode, useCallback, useState } from "react"
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  AutocompleteValue,
  Box,
  Checkbox,
  Chip,
  Divider,
  Popper,
  PopperProps,
  Stack,
  TextField,
  Typography,
  UseAutocompleteProps,
  useTheme,
  InputAdornment,
  FormControl,
  styled
} from "@mui/material"
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined"
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded"
import MobileAutocompleteModal from "./MobileAutocompleteModal"
import { useResponsive } from "src/utils/useResponsive"

const CustomAutocompleteChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  "& .MuiChip-label": { padding: theme.spacing(1, 2) }
}))

CustomAutocompleteChip.defaultProps = { size: "small", color: "primary" }

export { CustomAutocompleteChip }

export interface CustomAutocompleteProps<T>
  extends Omit<UseAutocompleteProps<T, boolean, boolean, boolean>, "getOptionLabel"> {
  label?: string
  getOptionLabel: (option: T) => string
  helperText?: ReactNode
  size?: "small" | "medium" | "extraSmall"
  hideInputText?: boolean
  renderTags?: (value: T[], getTagProps: AutocompleteRenderGetTagProps) => ReactNode
}

const CustomAutocomplete = <T,>({
  size,
  label,
  value,
  options,
  disabled,
  multiple,
  helperText,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  groupBy,
  hideInputText,
  disableClearable,
  renderTags: renderTagsProp,
  ...rest
}: CustomAutocompleteProps<T>) => {
  const [open, setOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { palette } = useTheme()
  const { isMobile } = useResponsive()

  const customPopper = useCallback((props: any) => <PopperWithTopMargin {...props} />, [])

  const handleMobileOpen = () => {
    if (disabled) return
    setMobileOpen(true)
  }

  const handleMobileClose = () => {
    setMobileOpen(false)
  }

  const handleMobileOptionSelect = (option: T) => {
    // Create a synthetic event-like object for compatibility
    const mockEvent = {} as React.SyntheticEvent<Element, Event>

    if (multiple) {
      const currentValue = (value as T[]) || []
      const isSelected = currentValue.some((item) => isOptionEqualToValue?.(item, option) ?? item === option)

      if (isSelected) {
        const newValue = currentValue.filter((item) => !(isOptionEqualToValue?.(item, option) ?? item === option))
        onChange?.(mockEvent, newValue as AutocompleteValue<T, boolean, boolean, boolean>, "removeOption", { option })
      } else {
        const newValue = [...currentValue, option]
        onChange?.(mockEvent, newValue as AutocompleteValue<T, boolean, boolean, boolean>, "selectOption", { option })
      }
    } else {
      onChange?.(mockEvent, option as AutocompleteValue<T, boolean, boolean, boolean>, "selectOption", { option })
      handleMobileClose()
    }
  }

  const handleMobileDone = () => {
    handleMobileClose()
  }

  const handleMobileClear = () => {
    const mockEvent = {} as React.SyntheticEvent<Element, Event>

    if (multiple) {
      onChange?.(mockEvent, [] as AutocompleteValue<T, boolean, boolean, boolean>, "clear")
    } else {
      onChange?.(mockEvent, null as AutocompleteValue<T, boolean, boolean, boolean>, "clear")
    }

    if (!multiple) {
      handleMobileClose()
    }
  }

  // If mobile, render click-to-open field + modal
  if (isMobile) {
    return (
      <>
        <FormControl fullWidth>
          <TextField
            size={size === "extraSmall" ? "small" : size}
            label={size === "extraSmall" ? undefined : label}
            helperText={helperText}
            disabled={disabled}
            onClick={handleMobileOpen}
            value={
              multiple
                ? ((value as T[]) || []).length > 0
                  ? getOptionLabel((value as T[])[0])
                  : ""
                : value
                ? getOptionLabel(value as T)
                : ""
            }
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {multiple && ((value as T[]) || []).length > 1 && (
                      <CustomAutocompleteChip label={`+${((value as T[]) || []).length - 1}`} />
                    )}
                    {!disabled && <KeyboardArrowDownRoundedIcon />}
                  </Stack>
                </InputAdornment>
              )
            }}
            sx={{
              cursor: disabled ? "default" : "pointer",
              "& .MuiInputBase-input": {
                cursor: disabled ? "default" : "pointer",
                ...(size === "extraSmall" && {
                  padding: "4px 8px"
                })
              }
            }}
          />
        </FormControl>
        <MobileAutocompleteModal
          open={mobileOpen}
          onClose={handleMobileClose}
          label={label}
          options={options}
          value={value}
          multiple={multiple}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          onOptionSelect={handleMobileOptionSelect}
          onDone={handleMobileDone}
          onClear={disableClearable ? undefined : handleMobileClear}
          palette={palette}
        />
      </>
    )
  }

  return (
    <Autocomplete
      size={size === "extraSmall" ? "small" : size}
      selectOnFocus={false}
      value={value}
      disabled={disabled}
      multiple={multiple}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableCloseOnSelect={multiple}
      limitTags={1}
      options={options}
      getOptionLabel={(option) => getOptionLabel(option as T)}
      onChange={onChange}
      renderOption={(props, option, { selected }) => (
        <Box
          component="li"
          {...props}
          sx={{
            height: "24px",
            "&.MuiAutocomplete-option.Mui-focused": {
              backgroundColor: "rgba(0, 210, 255, 0.08)"
            }
          }}
        >
          {multiple && (
            <Checkbox
              checked={selected}
              checkedIcon={<CheckBoxOutlinedIcon />}
              sx={{
                color: palette.action.disabled,
                "&.Mui-checked": {
                  color: palette.primary.main
                }
              }}
            />
          )}
          {getOptionLabel(option)}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            readOnly: hideInputText
          }}
          sx={
            hideInputText
              ? {
                  "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                    width: "1px",
                    minWidth: 0
                  }
                }
              : {}
          }
        ></TextField>
      )}
      PopperComponent={customPopper}
      renderTags={renderTagsProp ?? ((value, getTagProps) => {
        const numTags = value.length
        const limitTags = 1

        return (
          <Stack direction={"row"} alignItems={"center"} sx={{ pl: 0.5 }} onClick={() => setOpen(!open)}>
            <Typography sx={{ pl: 0, overflow: "hidden", maxWidth: "150px" }} noWrap>
              {getOptionLabel(value[0])}
            </Typography>
            {numTags > limitTags && <CustomAutocompleteChip label={` +${numTags - limitTags}`} />}
          </Stack>
        )
      })}
      isOptionEqualToValue={isOptionEqualToValue}
      popupIcon={<KeyboardArrowDownRoundedIcon />}
      groupBy={groupBy}
      renderGroup={(params) => (
        <li key={params.key}>
          {params.key != "0" && <Divider sx={{ mb: 1 }} />}
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      disableClearable={disableClearable}
      {...rest}
    ></Autocomplete>
  )
}
export default CustomAutocomplete

const PopperWithTopMargin = function (props: PopperProps) {
  const { palette } = useTheme()
  return (
    <Popper
      {...props}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 8]
          }
        }
      ]}
      sx={{
        border: "1px solid",
        borderColor: palette.action.disabled,
        boxShadow: "2px 2px 10px #0000001A",
        borderRadius: "6px"
      }}
    />
  )
}
